// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/date-range-picker.js
import { createPopper } from "@popperjs/core"
import BaseComponent from "./base_component"
import Calendar from "./calendar"
import { dateDisabled, dateRangeContainsDisabled, initialCalendarDate, localDate, parseDate, parseTime } from "./dom"

import {
  delayedInput,
  pickerLabel,
  button,
  twoDigits,
  timeFormat,
  parsePickerDate,
  attachMenu,
  showMenu,
  pickerTab,
  hideMenu,
} from "./picker-utils"
import TimePicker from "./time-picker"

export default class DateRangePicker extends BaseComponent {
  static componentName = "date-range-picker"
  static defaults = {
    calendars: 2,
    cancelButton: "Cancel",
    cleaner: true,
    confirmButton: "OK",
    disabled: false,
    firstDayOfWeek: 1,
    footer: false,
    indicator: true,
    inputReadOnly: false,
    locale: "default",
    placeholder: ["Start date", "End date"],
    range: true,
    ranges: {},
    required: true,
    separator: true,
    timepicker: false,
    hours: null,
    minutes: true,
    seconds: true,
  }

  constructor(element, config = {}) {
    super(element, config)
    if (!this.config.startDate && !this.config.range && this.config.date) this.config.startDate = this.config.date
    this.startDate = parseDate(this.config.startDate, this.config.selectionType)
    this.endDate = parseDate(this.config.endDate, this.config.selectionType)
    this.calendarDate = initialCalendarDate(this.config)
    this.mobile = window.innerWidth < 768
    this.initialDates = [this.startDate, this.endDate]
    this.selectEndDate = Boolean(this.config.selectEndDate)
    this.render()
    attachMenu(this)
    this.bindEvents()
  }

  render() {
    this.element.replaceChildren()
    this.element.className = this.originalClassName
    this.element.classList.add("date-picker")
    if (this.config.size) this.element.classList.add(`date-picker-${this.config.size}`)
    if (this.config.disabled) this.element.classList.add("disabled")
    this.element.dataset.coreuiToggle = this.config.range ? "date-range-picker" : "date-picker"
    this.element.setAttribute("aria-expanded", "false")
    this.element.append(this.createInputGroup())

    this.menu = document.createElement("div")
    this.menu.className = "date-picker-dropdown"
    this.menu.append(this.createBody())
    if (this.config.footer || this.config.timepicker) this.menu.append(this.createFooter())
    this.element.append(this.menu)
    this.renderCalendar()
  }

  placeholders() {
    if (Array.isArray(this.config.placeholder)) return this.config.placeholder
    return this.config.range
      ? [this.config.placeholder || "Start date", "End date"]
      : [this.config.placeholder || "Select date"]
  }

  createInput(date, name, placeholder) {
    const input = document.createElement("input")
    input.className = "date-picker-input"
    input.type = "text"
    input.autocomplete = "off"
    input.placeholder = placeholder
    input.disabled = Boolean(this.config.disabled)
    input.readOnly = Boolean(this.config.inputReadOnly)
    input.required = Boolean(this.config.required)
    input.value = this.format(date)
    if (name) input.name = name
    return input
  }

  createInputGroup() {
    const group = document.createElement("div")
    group.className = "date-picker-input-group"
    const names = [this.config.name || this.config.startName, this.config.endName]
    this.startInput = this.createInput(this.startDate, names[0], this.placeholders()[0])
    this.endInput = this.createInput(this.endDate, names[1], this.placeholders()[1] || "End date")
    const label = pickerLabel(this.element)
    if (label) {
      this.startInput.setAttribute("aria-label", this.config.range ? `${label}: start` : label)
      this.endInput.setAttribute("aria-label", `${label}: end`)
    }
    group.append(this.startInput)
    if (this.config.separator) group.insertAdjacentHTML("beforeend", '<div class="date-picker-separator"></div>')
    if (this.config.range) group.append(this.endInput)
    if (this.config.indicator) {
      this.indicator = document.createElement("div")
      this.indicator.className = "date-picker-indicator"
      if (!this.config.disabled) this.indicator.tabIndex = 0
      group.append(this.indicator)
    }
    if (this.config.cleaner) {
      const cleaner = document.createElement("div")
      cleaner.className = "date-picker-cleaner"
      cleaner.setAttribute("aria-label", "Clear dates")
      this.listen(cleaner, "click", (event) => {
        event.stopPropagation()
        this.clear()
      })
      group.append(cleaner)
    }
    this.toggler = group
    return group
  }

  createBody() {
    const body = document.createElement("div")
    body.className = "date-picker-body"
    if (Object.keys(this.config.ranges || {}).length) {
      const ranges = document.createElement("div")
      ranges.className = "date-picker-ranges"
      Object.entries(this.config.ranges).forEach(([label, dates]) => {
        ranges.append(button(this, label, "btn btn-ghost-secondary", () => this.setDates(dates[0], dates[1])))
      })
      body.append(ranges)
    }
    this.calendars = document.createElement("div")
    this.calendars.className = "date-picker-calendars"
    body.append(this.calendars)
    if (this.config.timepicker) body.append(this.createTimePickers())
    return body
  }

  renderCalendar() {
    this.calendar?.dispose()
    this.calendars.replaceChildren()
    const calendarElement = document.createElement("div")
    calendarElement.className = "date-picker-calendar"
    this.calendars.append(calendarElement)
    this.calendar = new Calendar(calendarElement, {
      ...this.config,
      calendars: this.mobile ? 1 : this.config.calendars,
      calendarDate: this.calendarDate,
      startDate: this.startDate,
      endDate: this.endDate,
      selectEndDate: this.selectEndDate,
      onSelect: (startDate, endDate, changed) => this.selected(startDate, endDate, changed),
    })
  }

  createTimePickers() {
    const container = document.createElement("div")
    container.className = "date-picker-timepickers"
    const create = (date, onChange) => {
      const element = document.createElement("div")
      element.className = "time-picker"
      const picker = new TimePicker(element, {
        type: "inline",
        variant: "select",
        footer: false,
        time: date,
        disabled: !date,
        locale: this.config.locale,
        hours: this.config.hours,
        minutes: this.config.minutes,
        seconds: this.config.minutes && this.config.seconds,
        onChange,
      })
      container.append(element)
      return picker
    }
    this.startTimePicker = create(this.startDate, (date) => this.copyTime(date, true))
    if (this.config.range) this.endTimePicker = create(this.endDate, (date) => this.copyTime(date, false))
    return container
  }

  createFooter() {
    const footer = document.createElement("div")
    footer.className = "date-picker-footer"
    footer.append(
      button(this, "Today", "btn btn-sm btn-primary me-auto", () =>
        this.setDates(new Date(), this.config.range ? new Date() : null),
      ),
    )
    if (this.config.cancelButton)
      footer.append(button(this, this.config.cancelButton, "btn btn-sm btn-ghost-primary", () => this.cancel()))
    if (this.config.confirmButton)
      footer.append(button(this, this.config.confirmButton, "btn btn-sm btn-primary", () => this.hide()))
    return footer
  }

  bindEvents() {
    this.listen(window, "resize", () => {
      const mobile = window.innerWidth < 768
      if (mobile === this.mobile) return
      this.mobile = mobile
      this.calendarDate = this.calendar.calendarDate
      this.renderCalendar()
      this.popper?.update()
    })
    this.listen(this.element, "click", (event) => {
      if (event.target === this.startInput || event.target === this.endInput) {
        this.selectEndDate = event.target === this.endInput
        this.calendar.update({ selectEndDate: this.selectEndDate })
      }
      if (event.target === this.indicator) this.toggle()
      else if (event.target.closest(".date-picker-input-group") && !event.target.closest(".date-picker-cleaner"))
        this.show()
    })
    delayedInput(this, this.startInput, () => {
      const date = parsePickerDate(this.startInput.value, this.config)
      if (date || !this.startInput.value.trim()) this.setDates(date, this.endDate)
    })
    if (this.config.range)
      delayedInput(this, this.endInput, () => {
        const date = parsePickerDate(this.endInput.value, this.config)
        if (date || !this.endInput.value.trim()) this.setDates(this.startDate, date)
      })
    const keydown = (event) => {
      pickerTab(this, event)
      if (event.key === "Escape") this.hide()
      if ((event.key === "Enter" || event.key === " ") && event.target === this.indicator) {
        event.preventDefault()
        this.toggle()
      }
    }
    this.listen(this.element, "keydown", keydown)
    if (this.detachedMenu) this.listen(this.menu, "keydown", keydown)
    this.listen(document, "click", (event) => {
      if (!event.composedPath().includes(this.element) && !this.menu.contains(event.target)) this.hide()
    })
  }

  selected(startDate, endDate, changed) {
    this.selectEndDate = this.calendar.selectEndDate
    this.startDate = startDate
    this.endDate = endDate
    this.updateInputs()
    for (const part of changed) this.emit(`${part}DateChange`, { date: this[`${part}Date`] })
    if (!this.config.range) this.emit("dateChange", { date: this.startDate })
    if (
      (!this.config.range || (changed.includes("end") && this.startDate)) &&
      !this.config.timepicker &&
      !this.config.footer
    )
      this.hide()
  }

  copyTime(time, start) {
    if (!time) return
    const current = start ? this.startDate : this.endDate
    const date = current ? new Date(current) : new Date()
    date.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0)
    this.setDates(start ? date : this.startDate, start ? this.endDate : date, false)
  }

  setDates(startDate, endDate = null, rerender = true) {
    const start = parseDate(startDate, this.config.selectionType)
    const end = this.config.range ? parseDate(endDate, this.config.selectionType) : null
    if (
      (start && dateDisabled(start, this.config)) ||
      (end && dateDisabled(end, this.config)) ||
      dateRangeContainsDisabled(start, end, this.config.disabledDates)
    )
      return
    this.startDate = start
    this.endDate = end
    if (this.startDate && this.endDate && this.endDate < this.startDate)
      [this.startDate, this.endDate] = [this.endDate, this.startDate]
    this.calendarDate = this.startDate || this.calendarDate
    this.updateInputs()
    if (rerender) this.renderCalendar()
    this.emit("startDateChange", { date: this.startDate })
    if (!this.config.range) this.emit("dateChange", { date: this.startDate })
    if (this.config.range) this.emit("endDateChange", { date: this.endDate })
  }

  updateInputs() {
    for (const [picker, date] of [
      [this.startTimePicker, this.startDate],
      [this.endTimePicker, this.endDate],
    ]) {
      if (!picker) continue
      picker.config.disabled = !date
      picker.element.classList.toggle("disabled", !date)
      picker.body.querySelectorAll("select").forEach((select) => {
        select.disabled = !date
      })
      if (date) {
        for (const [part, getter, setter, count] of [
          ["hours", "getHours", "setHours", 24],
          ["minutes", "getMinutes", "setMinutes", 60],
          ["seconds", "getSeconds", "setSeconds", 60],
        ]) {
          if (picker.config[part] === false) {
            date[setter](0)
            continue
          }
          const values = picker.availableValues(
            Array.from({ length: count }, (_, index) => index),
            part,
          )
          const value = date[getter]()
          if (values.length && !values.includes(value)) {
            date[setter](
              values.reduce((nearest, next) => (Math.abs(next - value) < Math.abs(nearest - value) ? next : nearest)),
            )
          }
        }
      }
      picker.date = date ? new Date(date) : null
      picker.updateSelection()
    }
    this.startInput.value = this.format(this.startDate)
    if (this.config.range) this.endInput.value = this.format(this.endDate)
  }

  format(date) {
    if (!date) return ""
    if (this.config.selectionType === "quarter") return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`
    const formatted = date.toLocaleDateString(this.config.locale, { year: "numeric", month: "numeric", day: "numeric" })
    return this.config.timepicker
      ? date.toLocaleString(this.config.locale, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          ...timeFormat(this.config),
        })
      : formatted
  }

  show() {
    if (this.config.disabled || this.element.classList.contains("show")) return
    this.initialDates = [this.startDate ? new Date(this.startDate) : null, this.endDate ? new Date(this.endDate) : null]
    showMenu(this)
    this.element.classList.add("show")
    this.element.setAttribute("aria-expanded", "true")
    this.emit("shown")
  }

  hide() {
    if (!this.element.classList.contains("show")) return
    hideMenu(this)
    this.element.classList.remove("show")
    this.element.setAttribute("aria-expanded", "false")
    this.emit("hidden")
  }

  toggle() {
    this.element.classList.contains("show") ? this.hide() : this.show()
  }

  clear() {
    this.setDates(null, null)
  }

  cancel() {
    this.setDates(this.initialDates[0], this.initialDates[1])
    this.hide()
  }

  dispose() {
    if (this.signal.aborted) return
    hideMenu(this)
    this.restoreAttribute("aria-expanded")
    this.restoreAttribute("data-coreui-toggle")
    this.calendar?.dispose()
    this.startTimePicker?.dispose()
    this.endTimePicker?.dispose()
    this.element.innerHTML = this.originalHTML
    this.element.className = this.originalClassName
    super.dispose()
  }
  snapshotState() {
    return {
      config: {
        date: this.startDate,
        startDate: this.startDate,
        endDate: this.endDate,
        calendarDate: this.calendarDate,
        selectEndDate: this.selectEndDate,
      },
      text: [this.startInput?.value, this.endInput?.value],
    }
  }
  restoreState(state) {
    if (!state.text) return
    if (this.startInput) this.startInput.value = state.text[0] || ""
    if (this.endInput) this.endInput.value = state.text[1] || ""
  }
}
